import { useEffect, useState } from 'react';
import CookieBanner from 'react-cookie-consent';
import { useSelector } from 'react-redux';
import { SCREENS } from '../services/exports/Constants';
import Link from './navigation/Link';
import ReduxActions from '../store/ReduxActions';
import { actionCreators } from '../store/actions';
import { useTranslation } from 'react-i18next';
import { StoreInterface } from '../store/types';
import { COOKIE_SETTINGS_EVENT, withdrawTrackingConsent } from '../services/helpers/TrackingConsent';
import { readCookieConsent } from '../utils/cookieConsent';

export default function CookieConsent() {
  const { t } = useTranslation(null, { keyPrefix: 'Components:CookieConsent' });
  const { cookiesConsent } = useSelector((state: StoreInterface) => state.appStatus);
  // "Cookie settings" (footer) reopens the banner after a choice, so consent can be withdrawn.
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);

  useEffect(() => {
    onChangeStatus();
  }, []);

  useEffect(() => {
    const open = () => setIsSettingsOpen(true);

    window.addEventListener(COOKIE_SETTINGS_EVENT, open);

    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, open);
  }, []);

  // Reopened from the footer: move focus into the banner so keyboard and screen-reader users land on it.
  useEffect(() => {
    if (!isSettingsOpen) {
      return;
    }

    document.getElementById('rcc-confirm-button')?.focus();
  }, [isSettingsOpen]);

  function onChangeStatus(): void {
    ReduxActions.dispatch(actionCreators.appStatus.setCookieConsent(readCookieConsent()));
  }

  function onAccept(): void {
    setIsSettingsOpen(false);
    onChangeStatus();
  }

  function onDecline(): void {
    const wasAccepted = cookiesConsent === 'true';

    setIsSettingsOpen(false);
    onChangeStatus();

    // GDPR Art. 7(3): declining after an earlier accept withdraws consent, so wipe what the trackers
    // stored and reload to unload their scripts.
    if (wasAccepted) {
      withdrawTrackingConsent();
    }
  }

  return (
    <div data-test="cookie-banner">
      <CookieBanner
        overlay
        flipButtons
        enableDeclineButton
        visible={isSettingsOpen ? 'show' : 'byCookieValue'}
        location={'bottom'}
        disableStyles
        buttonText={t('buttons.accept')}
        declineButtonText={t('buttons.reject')}
        overlayClasses="bg-black/60 dark:bg-[#343434]/60 h-screen w-screen top-0 left-0 fixed z-[9999]"
        containerClasses="bg-white dark:bg-black-600 h-fit shadow-xl !bg-brand-background-primary w-full flex flex-col justify-center rounded-t-2xl px-md pb-md pt-lg fixed bottom-0"
        contentClasses="my-auto text-center mb-md"
        buttonWrapperClasses="mx-auto flex flex-col gap-sm w-fit justify-center"
        buttonClasses="p-xs lg:px-xl rounded-md shadow-md dark:shadow-none flex flex-row items-center justify-center relative shadow text-brand-button-primary-text bg-brand-button-primary-background"
        declineButtonClasses="px-xs text-sm rounded-md py-[4px] hover:bg-brand-background-hover transition-all duration-200 ease-in-out flex flex-row items-center justify-center relative hover:bg-blue-300"
        onAccept={onAccept}
        onDecline={onDecline}
      >
        <h6 className="font-normal">
          {t('text')} <Link to={SCREENS.COOKIE_POLICY} text={t('cookie_policy')} />
        </h6>
      </CookieBanner>
    </div>
  );
}
